import React from 'react';
import { Link } from 'react-router-dom';
import {
    Book, TrendingUp, BarChart3, PieChart, Download, Smartphone,
    Lock, Filter, FileText, Calendar, CreditCard, Users,
    CheckCircle, ArrowRight, Zap, Shield
} from 'lucide-react';

const Features = () => {
    const features = [
        {
            icon: <Book className="h-8 w-8" />,
            title: "Unlimited Ledgers",
            description: "Create separate books for business, personal, projects, travel, farming - whatever you need. No limits.",
            color: "blue"
        },
        {
            icon: <TrendingUp className="h-8 w-8" />,
            title: "Income & Expense Tracking",
            description: "Log every transaction with amount, category, payment mode, and notes. Keep perfect records.",
            color: "green"
        },
        {
            icon: <BarChart3 className="h-8 w-8" />,
            title: "Powerful Reports",
            description: "Generate detailed financial reports with custom filters. Understand your money flow instantly.",
            color: "purple"
        },
        {
            icon: <PieChart className="h-8 w-8" />,
            title: "Visual Analytics",
            description: "Beautiful charts and graphs show spending patterns, category breakdowns, and trends over time.",
            color: "orange"
        },
        {
            icon: <Filter className="h-8 w-8" />,
            title: "Custom Categories",
            description: "Create categories that match your business. Retail, services, manufacturing - we adapt to you.",
            color: "pink"
        },
        {
            icon: <Download className="h-8 w-8" />,
            title: "Export Data",
            description: "Download reports in JSON or CSV format. Share with your accountant or use for tax filing.",
            color: "cyan"
        },
        {
            icon: <Smartphone className="h-8 w-8" />,
            title: "Mobile Responsive",
            description: "Works perfectly on desktop, tablet, and mobile. Track expenses on the go, anytime, anywhere.",
            color: "indigo"
        },
        {
            icon: <Lock className="h-8 w-8" />,
            title: "Bank-Level Security",
            description: "Your data is encrypted and protected. We follow strict security protocols to keep your information safe.",
            color: "red"
        },
        {
            icon: <Calendar className="h-8 w-8" />,
            title: "Date Range Filters",
            description: "View transactions for any period. Daily, weekly, monthly, or custom date ranges - your choice.",
            color: "yellow"
        },
        {
            icon: <CreditCard className="h-8 w-8" />,
            title: "Payment Mode Tracking",
            description: "Track whether you paid by cash, UPI, card, or bank transfer. Complete payment visibility.",
            color: "teal"
        },
        {
            icon: <FileText className="h-8 w-8" />,
            title: "Transaction Notes",
            description: "Add detailed notes to each transaction. Remember exactly what each expense was for.",
            color: "violet"
        },
        {
            icon: <Users className="h-8 w-8" />,
            title: "Multi-User Ready",
            description: "Each user gets their own secure account. Perfect for families or business partnerships.",
            color: "emerald"
        }
    ];

    const colorVariants = {
        blue: "from-blue-500 to-indigo-600",
        green: "from-green-500 to-emerald-600",
        purple: "from-purple-500 to-pink-600",
        orange: "from-orange-500 to-red-600",
        pink: "from-pink-500 to-rose-600",
        cyan: "from-cyan-500 to-blue-600",
        indigo: "from-indigo-500 to-purple-600",
        red: "from-red-500 to-pink-600",
        yellow: "from-yellow-500 to-orange-600",
        teal: "from-teal-500 to-cyan-600",
        violet: "from-violet-500 to-purple-600",
        emerald: "from-emerald-500 to-teal-600"
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Navigation */}
            <nav className="bg-white shadow-sm sticky top-0 z-50">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <Link to="/" className="flex items-center space-x-2">
                            <img
                                src="/softye_expense_tracker.png"
                                alt="Softye Logo"
                                className="w-10 h-10 object-contain"
                            />
                            <span className="text-xl font-bold text-gray-800">Softye Expense Tracker</span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link to="/login" className="text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg font-medium transition-colors">
                                Login
                            </Link>
                            <Link to="/signup" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                                Start Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="container mx-auto px-4 py-16">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                        ✨ 100% Free Features
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Everything You Need to Manage <span className="text-blue-600">Business Finances</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8">
                        Professional-grade features without the professional price tag. Every feature is completely free, forever.
                    </p>
                </div>
            </section>

            {/* Features Grid */}
            <section className="container mx-auto px-4 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100 group"
                        >
                            <div className={`w-16 h-16 bg-gradient-to-br ${colorVariants[feature.color]} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                            <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why Choose Section */}
            <section className="bg-gradient-to-br from-blue-600 to-indigo-700 py-20 text-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why MSMEs Choose Softye</h2>
                            <p className="text-blue-100 text-lg">More than just features - it's about making your life easier</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                                <Zap className="h-12 w-12 mb-4" />
                                <h3 className="text-2xl font-semibold mb-3">Lightning Fast</h3>
                                <p className="text-blue-100">
                                    No waiting, no loading screens. Add transactions in seconds, generate reports instantly. Your time is valuable.
                                </p>
                            </div>

                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                                <Shield className="h-12 w-12 mb-4" />
                                <h3 className="text-2xl font-semibold mb-3">Absolutely Secure</h3>
                                <p className="text-blue-100">
                                    Bank-level encryption, secure servers, regular backups. Your financial data is protected 24/7.
                                </p>
                            </div>

                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                                <Users className="h-12 w-12 mb-4" />
                                <h3 className="text-2xl font-semibold mb-3">Built for India</h3>
                                <p className="text-blue-100">
                                    Designed specifically for Indian MSMEs. INR currency, local payment modes, and features you actually need.
                                </p>
                            </div>

                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                                <CheckCircle className="h-12 w-12 mb-4" />
                                <h3 className="text-2xl font-semibold mb-3">Always Free</h3>
                                <p className="text-blue-100">
                                    No hidden costs, no upgrades, no premium tiers. Every feature you see is free, and will stay free forever.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perfect For Every Business</h2>
                        <p className="text-gray-600 text-lg">See how different businesses use Softye</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {[
                            { type: "Retail Shops", use: "Track daily sales, inventory costs, and supplier payments" },
                            { type: "Freelancers", use: "Manage multiple client projects and track business expenses" },
                            { type: "Service Providers", use: "Monitor service costs, client invoices, and operational expenses" },
                            { type: "Workshops", use: "Track spare parts, equipment maintenance, and labor costs" },
                            { type: "Manufacturers", use: "Monitor raw materials, production costs, and sales revenue" },
                            { type: "Farmers", use: "Track crop cycles, farming inputs, seeds, fertilizers, and market sales" }
                        ].map((item, idx) => (
                            <div key={idx} className="bg-gradient-to-br from-white to-blue-50 rounded-xl p-6 border border-blue-100">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-blue-600" />
                                    {item.type}
                                </h3>
                                <p className="text-gray-600 text-sm">{item.use}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gray-900 text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-4">Ready to Experience All Features?</h2>
                    <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                        Start using all features completely free. No credit card, no trials, no limits.
                    </p>
                    <Link
                        to="/signup"
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all hover:shadow-lg"
                    >
                        Start Free Now
                        <ArrowRight className="h-5 w-5" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-8 border-t border-gray-800">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="mb-4 md:mb-0">
                            <p className="text-gray-400">© 2024 Softye Technologies. All rights reserved.</p>
                        </div>
                        <div className="flex gap-6">
                            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
                            <Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link>
                            <Link to="/features" className="text-gray-400 hover:text-white transition-colors">Features</Link>
                            <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Features;
